import { z } from "zod/v4";

export const WorkflowAgentSchema = z.enum(["shell", "claude", "amp", "codex", "copilot"]);
export type WorkflowAgent = z.infer<typeof WorkflowAgentSchema>;

export const AGENT_LABELS: Record<WorkflowAgent, string> = {
	shell: "Shell Script",
	claude: "Claude",
	amp: "Amp",
	codex: "Codex",
	copilot: "Copilot",
};

export const workflowFormSchema = z.object({
	name: z.string().trim().min(1, { message: "Name is required" }),
	schedule: z.string().trim().min(1, { message: "Schedule is required" }),
	agent: WorkflowAgentSchema,
	prompt: z.string().trim().min(1, { message: "This field is required" }),
	targetDocumentId: z.string().min(1, { message: "Target document is required" }),
});

export const WorkflowSchema = z.object({
	apiVersion: z.string().default("v1alpha1"),
	id: z.string(),
	name: z.string(),
	schedule: z.string(),
	prompt: z.string(),
	agent: WorkflowAgentSchema.default("shell"),
	enabled: z.boolean().default(true),
	targetDocumentId: z.string(),
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type Workflow = z.infer<typeof WorkflowSchema>;

export const WorkflowRunSchema = z.object({
	id: z.string(),
	automationId: z.string(),
	status: z.enum(["pending", "running", "completed", "failed", "lost"]),
	output: z.string().optional(),
	error: z.string().optional(),
	startedAt: z.string(),
	completedAt: z.string().optional(),
});
export type WorkflowRun = z.infer<typeof WorkflowRunSchema>;

/**
 * Derived schedule state for a single workflow.
 *
 * Computed by `computeWorkflowSchedule()` from raw workflow + run data.
 * Consumed by both the scheduler effect and UI components.
 *
 * Three orthogonal concerns:
 * - `status`: what's happening right now (idle vs running)
 * - `lastRunStatus`: outcome of the most recent completed run
 * - `scheduleStatus`: whether the scheduler can fire ticks (active/paused/disabled)
 * - `isDue`: whether a cron tick was missed (pure clock math)
 */
export type WorkflowScheduleEntry = {
	workflow: Workflow;
	/** Most recent run for this workflow (newest first), if any. */
	latestRun: WorkflowRun | undefined;
	/** Next scheduled cron tick (from now), or null if schedule is invalid. */
	nextRun: Date | null;
	/** What's happening right now: idle or running. */
	status: "idle" | "running";
	/** Outcome of the most recent completed/failed run, if any. */
	lastRunStatus: "completed" | "failed" | "lost" | undefined;
	/** Whether the scheduler can fire ticks for this workflow. */
	scheduleStatus: "active" | "paused" | "disabled";
	/** Whether a cron tick was missed since the last run (or since creation). Pure clock math. */
	isDue: boolean;
};
